import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';

import type {
  BenchFilterInput,
  Rarity,
  RangeInput,
  SeasonEligibility,
  Sport,
  SupportedCurrency,
} from '#src/__generated__/globalTypes.ts';
import { namedOperations } from '#src/__generated__/operations.ts';
import type {
  FootballComposeFilters,
  NBAComposeFilters,
} from '#src/components/compose/types.ts';
import type {
  CreditCardPaymentMethod,
  PaymentMethod as DepositMethod,
} from '#src/components/wallet/BankEthAccounting/AddFundsToFiatWallet/types.tsx';
import type { AlgoliaCardIndexesName } from '#src/contexts/config/index.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import type {
  PaymentMethod,
  PaymentProvider_paymentMethod,
} from '#src/lib/paymentMethod.ts';
import type {
  PlayerSearchView,
  StatsRarity,
} from '#src/lib/playerSearchView.ts';

import type {
  UpdateLifecycleMutation,
  UpdateLifecycleMutationVariables,
} from './__generated__/useLifecycle.graphql.ts';
import { useIsMobileApp } from './useIsMobileApp.ts';

export enum LIFECYCLE {
  sawManagersSalesOnboarding = 'sawManagersSalesOnboarding',
  transferMarket = 'transferMarket',
  newSignings = 'newSignings',
  nextTimeUserNeedsCheckSecurity = 'nextTimeUserNeedsCheckSecurity',
  nextTimeUserShouldSeeActivateFiatWalletOnListing = 'nextTimeUserShouldSeeActivateFiatWalletOnListing',
  nextTimeUserShouldSeeAcceptedCurrenciesOnListing = 'nextTimeUserShouldSeeAcceptedCurrenciesOnListing',
  userHasSetupWallet = 'userHasSetupWallet',
  nextTimeUserShouldSetupWallet = 'nextTimeUserShouldSetupWallet',
  lastSaleDuration = 'lastSaleDuration',
  inSeasonFilterEnabled = 'inSeasonFilterEnabled',
  latestSearchedItems = 'latestSearchedItems',
  marketSorts = 'marketSorts',
  hideActivity = 'hideActivity',
  disable3D = 'disable3D',
  hideOnlineStatus = 'hideOnlineStatus',
  preferredPaymentMethod = 'preferredPaymentMethod',
  preferredDepositMethod = 'preferredDepositMethod',
  referenceCurrencyForSale = 'referenceCurrencyForSale',
  hideReferralBanner = 'hideReferralBanner',
  doNotShowAddAuctionReminderDownloadApp = 'doNotShowAddAuctionReminderDownloadApp',
  unselectConversionCreditByDefaultFromSettings = 'unselectConversionCreditByDefaultFromSettings',
  hideConversionCreditBanner = 'hideConversionCreditBanner',
  sawComposeTeamHighlights = 'sawComposeTeamHighlights',
  sawComposeTeamBenchFooterOnboarding = 'sawComposeTeamBenchFooterOnboarding',
  sawTrainingComposeOnboarding = 'sawTrainingComposeOnboarding',
  sawPushNotificationsPromptInWebview = 'sawNotificationsPromptInWebview',
  sawSeriesHotStreakOnboarding = 'sawSeriesHotStreakOnboarding',
  sawSeriesBoardOnboarding = 'sawSeriesBoardOnboarding',
  sawSeriesScheduleScreen = 'sawSeriesScheduleScreen',
  sawSetLineupOnboarding = 'sawSetLineupOnboarding',
  sawSeriesMarketOnboarding = 'sawSeriesMarketOnboarding',
  sawMVPOnboarding = 'sawMVPOnboarding',
  sawMyCardsOnboarding = 'sawMyCardsOnboarding',
  lastVisitedMyCardsRarity = 'lastVisitedMyCardsRarity',
  sawComposeTeamTutorial = 'sawComposeTeamTutorial',
  statsManager = 'statsManager',
  sawFirstTimeStatsManager = 'sawFirstTimeStatsManager',
  hasDismissedExpiredPromoDepositBanner = 'hasDismissedExpiredPromoDepositBanner',
  composeLastViewUsed = 'composeLastViewUsed',
  composeFilters = 'composeFilters',
  seriesComposeFilters = 'seriesComposeFilters',
  proComposeFilters = 'proComposeFilters',
  sawDropOnboarding = 'sawDropOnboarding',
  sawMLBComposeTeamHighlights = 'sawMLBComposeTeamHighlights',
  sawDropChallengePickerOnboarding = 'sawDropChallengePickerOnboarding',
  sawMlbSealingOnboarding = 'sawMlbSealingOnboarding',
  lastDismissedSorareWatchlists = 'lastDismissedSorareWatchlists',
  sawComposeTeamAdvancedViewOnboarding = 'sawComposeTeamAdvancedViewOnboarding',
  sawLineViewOnboarding = 'sawLineViewOnboarding',
  pinnedShardRarity = 'pinnedShardRarity',
  pinnedXpRarity = 'pinnedXpRarity',
  playerPageFilters = 'playerPageFilters',
  missionsRarityFilter = 'missionsRarityFilter',
  newFootballSeasonRarityFilter = 'footballNewSeasonRarityFilter',
  sawDepositButton = 'sawDepositButton',
  experimentalLightMode = 'lightMode',
  matchCenterFilters = 'matchCenterFilters',
  marketHomeFilters = 'marketHomeFilters',
  lastVisitedPlayTab = 'lastVisitedPlayTab',
  lastVisitedBoardViewV2 = 'lastVisitedBoardViewV2',
  sawCommonMissionsTutorial = 'sawCommonMissionsTutorial',
  sawSolanaFtue = 'sawSolanaFtue',
  storedLastNavigation = 'storedLastNavigation',
  welcomePackAutoOpen = 'welcomePackAutoOpen',
  sawJonumVerifiedDialog = 'sawJonumVerifiedDialog',
  dismissedMissionHighlightBannerSlugs = 'dismissedMissionHighlightBannerSlugs',
  goldenScoreIntroLastGameSeen = 'goldenScoreIntroLastGameSeen',
  sawBridgeToProBanner = 'sawBridgeToProBanner',
  sawSignFirstPlayersDialog = 'sawSignFirstPlayersDialog',
  lastMissionsClickAt = 'lastMissionsClickAt',
  sawRareBonusRewardTutorial = 'sawRareBonusRewardTutorial',
  sawCardPackShare = 'sawCardPackShare',
}

type SeriesComposeFilters = {
  averageScoreRange: Nullable<RangeInput>;
  showAffordableOnly: boolean;
  includeUnavailablePlayers: boolean;
  includeUsed: boolean;
  startersOnly: boolean;
  starterOddsBasisPointsRange: { min: number; max: number };
  enablePlayingInFilter: boolean;
  playingInHours: number;
  sortType: BenchFilterInput['sortType'];
};

type ProComposeFilters = SeriesComposeFilters & {
  playingInMinHours: number;
};

export type LifecycleEntries = {
  [LIFECYCLE.sawManagersSalesOnboarding]?: boolean;
  [LIFECYCLE.transferMarket]?: string;
  [LIFECYCLE.newSignings]?: string;
  [LIFECYCLE.nextTimeUserNeedsCheckSecurity]?: string;
  [LIFECYCLE.nextTimeUserShouldSeeActivateFiatWalletOnListing]?: string;
  [LIFECYCLE.nextTimeUserShouldSeeAcceptedCurrenciesOnListing]?: string;
  [LIFECYCLE.userHasSetupWallet]?: boolean;
  [LIFECYCLE.nextTimeUserShouldSetupWallet]?: string;
  [LIFECYCLE.lastSaleDuration]?: number;
  [LIFECYCLE.inSeasonFilterEnabled]?: boolean;
  [LIFECYCLE.latestSearchedItems]?: {
    [sport: string]: {
      objectID: string;
      index: string;
      sport: Sport;
    }[];
  };
  [LIFECYCLE.marketSorts]?: Record<string, AlgoliaCardIndexesName>;
  [LIFECYCLE.hideActivity]?: boolean;
  [LIFECYCLE.disable3D]?: boolean;
  [LIFECYCLE.hideOnlineStatus]?: boolean;
  [LIFECYCLE.referenceCurrencyForSale]: SupportedCurrency;
  [LIFECYCLE.preferredPaymentMethod]?:
    | PaymentProvider_paymentMethod
    | PaymentMethod.CRYPTO_WALLET
    | PaymentMethod.FIAT_WALLET
    // @deprecated Use CRYPTO_WALLET instead. Kept for backward compatibility
    | PaymentMethod.ETH_WALLET;
  [LIFECYCLE.preferredDepositMethod]?: DepositMethod | CreditCardPaymentMethod;
  [LIFECYCLE.hideReferralBanner]?: boolean;
  [LIFECYCLE.doNotShowAddAuctionReminderDownloadApp]?: boolean;

  [LIFECYCLE.unselectConversionCreditByDefaultFromSettings]?: boolean;
  [LIFECYCLE.hideConversionCreditBanner]?: boolean;
  [LIFECYCLE.sawComposeTeamHighlights]?: boolean;
  [LIFECYCLE.sawComposeTeamBenchFooterOnboarding]?: boolean;
  [LIFECYCLE.sawTrainingComposeOnboarding]?: {
    trackSlug?: string;
    score?: boolean;
    bonus?: boolean;
    chart?: boolean;
  };
  [LIFECYCLE.sawPushNotificationsPromptInWebview]?: boolean;
  [LIFECYCLE.sawMVPOnboarding]?: boolean;
  [LIFECYCLE.sawMyCardsOnboarding]?: boolean;
  [LIFECYCLE.lastVisitedMyCardsRarity]?: {
    [sport: string]: string;
  };
  [LIFECYCLE.lastVisitedPlayTab]?: {
    [sport: string]: 'play';
  };
  [LIFECYCLE.lastVisitedBoardViewV2]?: {
    [sport: string]: {
      lastVisitedMode?:
        | 'PRO'
        | 'ARENA_IN_SEASON'
        | 'ARENA_CLASSIC'
        | 'ARENA_CLASSIC_ROOMS';
      modes?: {
        PRO?: {
          lastVisitedBoardTitle?: string;
        };
        ARENA_IN_SEASON?: {
          lastVisitedSo5Competition?: string;
          lastVisitedArenaRarity?: Rarity;
        };
        ARENA_CLASSIC?: {
          lastVisitedSo5Competition?: string;
          lastVisitedArenaRarity?: Rarity;
        };
        ARENA_CLASSIC_ROOMS?: {
          lastVisitedSo5Competition?: string;
          lastVisitedArenaRarity?: Rarity;
        };
      };
    };
  };
  [LIFECYCLE.sawComposeTeamTutorial]?: boolean;
  [LIFECYCLE.statsManager]?: Record<Sport, PlayerSearchView>;
  [LIFECYCLE.sawFirstTimeStatsManager]?: Record<Sport, boolean>;
  [LIFECYCLE.playerPageFilters]?: Record<
    Sport,
    { rarity: StatsRarity; seasonality: SeasonEligibility }
  >;
  [LIFECYCLE.hasDismissedExpiredPromoDepositBanner]?: boolean;
  [LIFECYCLE.composeLastViewUsed]?: string;
  [LIFECYCLE.composeFilters]?: {
    [Sport.FOOTBALL]?: Pick<
      FootballComposeFilters,
      | 'sortType'
      | 'includeNoGame'
      | 'includeUsed'
      | 'startersOnly'
      | 'starterOddsBasisPointsRange'
    >;
    [Sport.NBA]?: Pick<NBAComposeFilters, 'includeNoGame' | 'includeUsed'>;
    [Sport.BASEBALL]?: null;
  };
  [LIFECYCLE.seriesComposeFilters]?: {
    [key in Sport]?: Partial<SeriesComposeFilters>;
  };
  [LIFECYCLE.proComposeFilters]?: {
    [key in Sport]?: Partial<ProComposeFilters>;
  };
  [LIFECYCLE.sawDropOnboarding]?: boolean;
  [LIFECYCLE.sawMLBComposeTeamHighlights]?: boolean;
  [LIFECYCLE.sawDropChallengePickerOnboarding]?: boolean;
  [LIFECYCLE.sawMlbSealingOnboarding]?: boolean;
  [LIFECYCLE.lastDismissedSorareWatchlists]?: Record<Sport, string>;
  [LIFECYCLE.sawComposeTeamAdvancedViewOnboarding]?: boolean;
  [LIFECYCLE.sawLineViewOnboarding]?: boolean;
  [LIFECYCLE.pinnedShardRarity]?: string | false;
  [LIFECYCLE.pinnedXpRarity]?: Rarity | false;
  [LIFECYCLE.missionsRarityFilter]?: {
    [key in Sport]: Rarity;
  };
  [LIFECYCLE.newFootballSeasonRarityFilter]?: Rarity;
  [LIFECYCLE.sawDepositButton]?: boolean;
  [LIFECYCLE.experimentalLightMode]?: boolean;
  [LIFECYCLE.matchCenterFilters]?: {
    sort: 'date' | 'competition';
    withCards: boolean;
  };
  [LIFECYCLE.marketHomeFilters]?: {
    [key in Sport]: {
      rarity: Rarity;
      competitionSlug: string;
    };
  };
  [LIFECYCLE.sawCommonMissionsTutorial]?: boolean;
  [LIFECYCLE.sawSolanaFtue]?: boolean;
  [LIFECYCLE.sawSeriesHotStreakOnboarding]?: boolean;
  [LIFECYCLE.sawSeriesBoardOnboarding]?: boolean;
  [LIFECYCLE.sawSeriesScheduleScreen]?: boolean;
  [LIFECYCLE.sawSetLineupOnboarding]?: boolean;
  [LIFECYCLE.sawSeriesMarketOnboarding]?: boolean;
  [LIFECYCLE.storedLastNavigation]?: Record<string, unknown>;
  [LIFECYCLE.welcomePackAutoOpen]?: { [slug: string]: Date };
  [LIFECYCLE.sawJonumVerifiedDialog]?: boolean;
  [LIFECYCLE.dismissedMissionHighlightBannerSlugs]?: string[];
  [LIFECYCLE.goldenScoreIntroLastGameSeen]?: string;
  [LIFECYCLE.sawBridgeToProBanner]?: boolean;
  [LIFECYCLE.sawSignFirstPlayersDialog]?: boolean;
  [LIFECYCLE.lastMissionsClickAt]?: {
    [key in Sport]?: string;
  };
  [LIFECYCLE.sawRareBonusRewardTutorial]?: {
    [key in Sport]?: boolean;
  };
  [LIFECYCLE.sawCardPackShare]?: boolean;
};

export type Lifecycle = LifecycleEntries | undefined;
export type LifecycleValue = LifecycleEntries[LIFECYCLE];
export type LifecycleGetter = LifecycleValue;

// oxlint-disable-next-line sorare/no-unnormalized-object-in-array -- pre-existing, unrelated to this change
const UPDATE_LIFECYCLE_MUTATION = gql`
  mutation UpdateLifecycleMutation($input: updateUserSettingsInput!) {
    updateUserSettings(input: $input) {
      userSettings {
        id
        lifecycle
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  UpdateLifecycleMutation,
  UpdateLifecycleMutationVariables
>;

export /**
 * We have a parameter here to refetch the configQuery as we do not want to load
 * currentUser in this mutation, as optimisticResponse can cause stale data in
 * Apollo Cache, which breaks field policies (and results in Date objects from
 * GraphQL being returned as strings)
 * MR: https://gitlab.com/sorare/frontend/-/merge_requests/19283
 * Works around: https://github.com/apollographql/apollo-client/issues/9293
 */
const useLifecycle = ({ refetchConfigQuery = false } = {}) => {
  const { currentUser } = useConfigContext();
  const { postMessage, isMobileApp } = useIsMobileApp();
  const [updateLifecycle, { loading }] = useMutation(
    UPDATE_LIFECYCLE_MUTATION,
    {
      refetchQueries: refetchConfigQuery
        ? [namedOperations.Query.ConfigQuery]
        : undefined,
    }
  );

  const update = useCallback(
    async <K extends keyof LifecycleEntries>(
      name: K,
      value: Omit<LifecycleEntries[K], 'undefined'>
    ) => {
      if (!currentUser?.slug) {
        return;
      }
      const lifecycle = currentUser.userSettings.lifecycle as Lifecycle;
      await updateLifecycle({
        variables: {
          input: {
            lifecycle: {
              name,
              value,
            },
          },
        },
        optimisticResponse: {
          updateUserSettings: {
            __typename: 'updateUserSettingsPayload',
            userSettings: {
              __typename: 'UserSettings',
              id: currentUser.userSettings.id,
              lifecycle: {
                ...lifecycle,
                [name]: value,
              },
            },
            errors: [],
          },
        },
      });

      if (isMobileApp) {
        postMessage('updateUserLifecycle', {
          updatedKey: name,
        });
      }
    },
    [updateLifecycle, currentUser, postMessage, isMobileApp]
  );

  const lifecycle = currentUser?.userSettings.lifecycle as Lifecycle;

  return {
    lifecycle,
    update,
    loading,
  };
};
